import React, { FC } from 'react';
import cn from 'classnames';
import { useTheme } from '../../../../../theme';
import Header from './components/Header/Header';
import RecommendedBadge from './components/RecommendedBadge/RecommendedBadge';
import { FareFfpBonusInfo, FareGroupVariant } from '../../model/types';
import GridOptions from './components/GridOptions/GridOptions';
import ListOptions from './components/ListOptions/ListOptions';
import FareButton from './components/FareButton/FareButton';
import FooterLinks from './components/FooterLinks/FooterLinks';
import { AviaPassengerFareFragment, FareFamilyFragment } from '@websky/graphql';
import { Money as MoneyType } from '../../../../../types';
import { UpgradeOption } from '../../../../../DesktopFlight/types';
import Upgrade from './components/Upgrade/Upgrade';
import { useFareOptions } from '../../model/useFareOptions';
import Taxes from './components/Taxes/Taxes';
import Notification from '../../../../../BaseComponents/Notification';
import { useTranslation } from 'react-i18next';
import FfpBonusInfoBlock from './components/FfpBonusInfoBlock/FfpBonusInfoBlock';
import { buildDataAttributes } from '@shared/lib/dom';

export interface Props {
	/* Style */
	className?: string;
	/* Data */
	fare: FareFamilyFragment;
	passengerFares?: AviaPassengerFareFragment[];
	/* Settings */
	variant: FareGroupVariant;
	recommended?: boolean;
	showUnavailableOptions?: boolean;
	disabled?: boolean;
	/* Price */
	price: MoneyType;
	originalPrice?: MoneyType;
	priceInMiles?: number;
	disabledForMiles?: boolean;
	/* Subscribe */
	subscribed?: boolean;
	subscribeOption?: UpgradeOption;
	onSubscribe?: () => void;
	/* Upgrade */
	showUpgradeOptions?: boolean;
	upgradeOption?: UpgradeOption;
	onUpgrade?: () => void;
	/* Selected */
	selected?: boolean;
	onSelect?: () => void;
	/* Footer links */
	onClickRules?: () => void;
	onClickConditions?: () => void;
	/* Miles to be credited info */
	ffpBonusInfo?: FareFfpBonusInfo;
	/* fare rules loading now */
	isFareRulesDisabled?: boolean;
	dataTestId?: string;
	dataVariant?: 'small' | 'big' | 'custom';
}

const FareGroupV2: FC<Props> = ({
	className,
	fare,
	passengerFares,
	variant,
	recommended,
	showUnavailableOptions,
	disabled,
	price,
	originalPrice,
	priceInMiles,
	disabledForMiles,
	subscribed,
	subscribeOption,
	onSubscribe,
	showUpgradeOptions,
	upgradeOption,
	onUpgrade,
	selected,
	onSelect,
	onClickRules,
	onClickConditions,
	ffpBonusInfo,
	isFareRulesDisabled,
	dataTestId,
	dataVariant
}) => {
	const { t } = useTranslation('FareGroup');
	const { FareGroupV2: css } = useTheme('Entities').FareGroupV2;

	const { title, options } = fare;

	const { gridOptions, listOptions } = useFareOptions(options, variant, showUnavailableOptions);

	const showRecommended = recommended && !disabled && !selected;
	const showGridOptions = gridOptions.length;
	const showListOptions = variant === 'small' && listOptions.length > 0;
	const showSubscribe = !subscribed && !!subscribeOption && !!onSubscribe && !disabled;
	const showUpgrade =
		showUpgradeOptions &&
		upgradeOption &&
		!!onUpgrade &&
		(upgradeOption?.canUpgradeTo?.prices || upgradeOption?.canUpgradeTo?.flights) &&
		!disabled;
	const showFooterLinks = (!!onClickRules || !!onClickConditions) && !disabled;
	const showTaxes = !!priceInMiles && !disabledForMiles && !disabled;
	const showDisabledForMiles = disabledForMiles && !disabled;
	const showFfpBonusInfo = !!ffpBonusInfo && !disabled;

	const handleOnSelect = (event: React.MouseEvent<HTMLButtonElement> | React.TouchEvent<HTMLDivElement>) => {
		event.preventDefault();
		event.stopPropagation();

		if (!disabled && onSelect) {
			onSelect();
		}
	};

	return (
		<div
			{...buildDataAttributes({
				'fare-id': fare.id,
				'test-id': dataTestId,
				variant: dataVariant || variant,
				selected,
				disabled,
				recommended
			})}
			className={cn(css.root, className, css[variant], {
				[css.recommended]: recommended,
				[css.disabled]: disabled,
				[css.selected]: selected
			})}
		>
			{showRecommended && <RecommendedBadge />}

			<div className={css.block__top}>
				<Header
					title={title}
					disabled={disabled}
					price={price}
					originalPrice={originalPrice}
					priceInMiles={priceInMiles}
				/>

				{showGridOptions && <GridOptions variant={variant} options={gridOptions} />}

				{showListOptions && <ListOptions options={listOptions} />}
			</div>

			<div className={css.block__bottom}>
				{showFfpBonusInfo && <FfpBonusInfoBlock {...ffpBonusInfo} />}

				{showSubscribe && (
					<Upgrade variant="subscribe" upgradeOption={subscribeOption} onUpgrade={onSubscribe} />
				)}

				{showUpgrade && (
					<Upgrade
						variant="upgrade"
						fareFamilyId={fare.id}
						upgradeOption={upgradeOption}
						onUpgrade={onUpgrade}
					/>
				)}

				<FareButton
					className={css.button}
					price={price}
					originalPrice={originalPrice}
					priceInMiles={priceInMiles}
					passengerFares={passengerFares}
					disabled={disabled}
					selected={selected}
					subscribed={subscribed}
					onClick={handleOnSelect}
				/>

				{showDisabledForMiles && (
					<Notification
						className={css.notification}
						type={'context'}
						status={'warning'}
						text={t('Not enough miles on your card')}
					/>
				)}

				{showFooterLinks && (
					<FooterLinks
						isFareRulesDisabled={isFareRulesDisabled}
						onClickRules={onClickRules}
						onClickConditions={onClickConditions}
					/>
				)}

				{showTaxes && <Taxes price={price} />}
			</div>
		</div>
	);
};

export default FareGroupV2;
